import dayjs from 'dayjs'
import ReactInputMask from 'react-input-mask'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'react-toastify'
import { isAxiosError } from 'axios'

import { Input } from '../../../../../../components/FormElements/Input'
import { Button } from '../../../../../../components/Button'
import { api } from '../../../../../../services/api'
import { painelNaturalPersonSchema } from '../../../../../../validators/painelNaturalPersonSchema'
import { formatDocument } from '../../../../../../utils/document'
import { FormGroup } from '../../../../../../components/FormElements/FormGroup'
import { InfiniteSelect } from '../../../../../../components/InfiniteSelect'
import { type ExpertDomain } from '../../../../../../types/domain/expert-domain'
import {
  useExpertMutation,
  type UpdateExpertParams,
} from '../../../../../../services/hooks/experts/mutations/useExpertMutation'
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../../../../../constants/toast'
import { getErrorMessage } from '../../../../../../utils/getErrorMessage'
import { useInfiniteResearchersSelectOptions } from './useInfiniteResearchersSelectOptions'
import { isEmailAvailable } from '../../../../../../utils/isEmailAvailable'
import { isCpfAvailable } from '../../../../../../utils/isCpfAlreadyInUse'
import { normalizeCpf } from '../../../../../../utils/normalizeCpf'
import { Radio } from '../../../../../../components/Radio'
import { SwitchExpertType } from '../../components/SwitchExpertType'
import clsx from 'clsx'

interface Props {
  data?: ExpertDomain
  save: () => void
}

type FormValues = {
  name: string
  personType: string
  email: string
  cpf: string | null
  phone: string
  researcherId?: number
  researcherAcronym?: string
  doNotContactTemporarily?: boolean | string
  agreeParticipateMeet?: boolean | string
  lastContactAt?: string
  linkedin?: string
  addressNumber: string
  birthdate: string
  cep: string
  complement: string
  neighborhood: string
  rg: string
  state: string
  city: string
  street: string
}

export const NaturalPersonForm = ({ data, save }: Props) => {
  const { mutate, isLoading } = useExpertMutation(data!.id)

  const [searchResearch, setSearchResearch] = useState('')

  const agreeParticipateMeetDefaultValue =
    typeof data?.agreeParticipateMeet !== 'boolean'
      ? ''
      : data.agreeParticipateMeet
        ? 'true'
        : 'false'
  const doNotContactTemporarilyDefaultValue =
    typeof data?.doNotContactTemporarily !== 'boolean'
      ? ''
      : data.doNotContactTemporarily
        ? 'true'
        : 'false'

  const {
    control,
    formState,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
  } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(painelNaturalPersonSchema),
    defaultValues: {
      cpf: data?.cpf?.replace(/^(\d{3})(\d{3})(\d{3})(\d)/, '$1.$2.$3-$4'),
      name: data?.name,
      personType: data?.personType,
      email: data?.email,
      phone: data?.phone,
      addressNumber: data?.naturalData?.addressNumber,
      birthdate: data?.naturalData?.birthdate,
      rg: data?.naturalData?.rg,
      linkedin: data?.linkedin,
      cep: data?.naturalData?.cep,
      street: data?.naturalData?.street,
      complement: data?.naturalData?.complement || '',
      neighborhood: data?.naturalData?.neighborhood,
      city: data?.naturalData?.city,
      state: data?.naturalData?.state,
      researcherId: data?.researcher?.id,
      researcherAcronym: data?.researcherAcronym,
      agreeParticipateMeet: agreeParticipateMeetDefaultValue,
      doNotContactTemporarily: doNotContactTemporarilyDefaultValue,
      lastContactAt: data?.lastContactAt,
    },
  })

  const { options, hasNextPage, fetchNextPage } =
    useInfiniteResearchersSelectOptions(searchResearch, data?.researcher)

  const validateCpf = async (cpf: string) => {
    try {
      const { data } = await api.get(
        `/experts/validate-cpf/${formatDocument(cpf)}`
      )

      setValue('name', data.name)
      setValue('birthdate', data.birthdate)
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.data.message === 'CPF not found') {
          setError('cpf', { message: 'CPF inválido' })
          return
        }
      }

      toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
    }
  }

  const validateCep = async (cep: string) => {
    try {
      const formattedCepFieldValue = formatDocument(cep)

      if (formattedCepFieldValue) {
        const { data } = await api.get(
          `https://viacep.com.br/ws/${formattedCepFieldValue}/json/`
        )

        if (!data.erro) {
          setValue('street', data.logradouro)
          setValue('city', data.localidade)
          setValue('state', data.uf)
          setValue('neighborhood', data.bairro)
          clearErrors('cep')
          return
        }

        setValue('city', '')
        setValue('state', '')
        setValue('neighborhood', '')
        setValue('street', '')
        setError('cep', { message: 'CEP inválido' })
      }
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
    }
  }

  const handleEditNaturalPersonData = async (formValues: FormValues) => {
    const formattedData: UpdateExpertParams = {
      name: formValues.name,
      personType: formValues.personType,
      email: formValues.email,
      cpf: normalizeCpf(formValues.cpf),
      phone: formValues.phone,
      researcherId: formValues.researcherId,
      researcherAcronym: formValues.researcherAcronym,
      agreeParticipateMeet: formValues.agreeParticipateMeet,
      doNotContactTemporarily: formValues.doNotContactTemporarily,
      lastContactAt: formValues.lastContactAt,
      linkedin: formValues.linkedin,
      naturalData: {
        addressNumber: formValues.addressNumber,
        birthdate: formValues.birthdate,
        cep: formValues.cep,
        complement: formValues.complement,
        neighborhood: formValues.neighborhood,
        rg: formValues.rg,
        state: formValues.state,
        city: formValues.city,
        street: formValues.street,
      },
    }

    mutate(
      { id: data!.id, data: formattedData },
      {
        onSuccess () {
          save()
          toast('Dados salvos com sucesso!', TOAST_SUCCESS_DEFAULT_OPTIONS)
        },
        onError (error) {
          toast(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      }
    )
  }

  return (
    <div>
      <form onSubmit={handleSubmit(handleEditNaturalPersonData)}>
        <SwitchExpertType control={control} personType={data?.personType} />

        <div
          className={clsx('w-full flex flex-col gap-[20px]', {
            hidden: watch('personType') !== 'natural_person',
          })}
        >
          <div className="w-36 md:w-44">
            <Controller
              name="cpf"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <ReactInputMask
                  mask="999.999.999-99"
                  maskPlaceholder={null}
                  value={value || ''}
                  onChange={async (event) => {
                    onChange(event)

                    const currentValue = event.target.value
                    const cpfPattern = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/

                    if (cpfPattern.test(currentValue)) {
                      if (await isCpfAvailable(currentValue, data?.cpf)) {
                        clearErrors('cpf')
                        validateCpf(currentValue)
                        return
                      }

                      setError('cpf', {
                        message: 'Já existe uma conta com este CPF',
                      })
                    }
                  }}
                >
                  <Input
                    label="CPF"
                    placeholder="000.000.000-00"
                    isValid={false}
                    errorMessage={error?.message}
                  />
                </ReactInputMask>
              )}
            />
          </div>

          <Controller
            name="name"
            control={control}
            render={({
              field: { value = '', onChange },
              fieldState: { error },
            }) => (
              <Input
                label="Nome completo"
                placeholder="Nome completo"
                isValid={false}
                value={value}
                onChange={onChange}
                errorMessage={error?.message}
              />
            )}
          />

          <div className="w-36 md:w-44">
            <Controller
              name="birthdate"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <ReactInputMask
                  mask="99/99/9999"
                  maskPlaceholder={null}
                  value={value}
                  onChange={onChange}
                >
                  <Input
                    label="Data de nascimento"
                    placeholder="00/00/0000"
                    isValid={false}
                    errorMessage={error?.message && `${error?.message}`}
                  />
                </ReactInputMask>
              )}
            />
          </div>

          <div className="w-36 md:w-44">
            <Controller
              name="rg"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <ReactInputMask
                  mask=""
                  maskPlaceholder={null}
                  value={value}
                  onChange={onChange}
                >
                  <Input
                    label="RG"
                    placeholder="00.000.000-0"
                    isValid={false}
                    errorMessage={error?.message && `${error?.message}`}
                  />
                </ReactInputMask>
              )}
            />
          </div>

          <div className="flex flex-col gap-5 md:flex-row md:gap-2">
            <div className="md:grow">
              <Controller
                name="email"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    label="E-mail"
                    placeholder="exemple@email.com"
                    isValid={false}
                    value={value}
                    onChange={async (event) => {
                      onChange(event)

                      if (
                        !(await isEmailAvailable(
                          event.target.value,
                          data?.email
                        ))
                      ) {
                        setError('email', { message: 'E-mail já em uso' })
                        return
                      }

                      clearErrors('email')
                    }}
                    errorMessage={error?.message && `${error?.message}`}
                  />
                )}
              />
            </div>
            <div className="w-36 md:w-48">
              <Controller
                name="phone"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => (
                  <ReactInputMask
                    mask="(99) 99999-9999"
                    maskPlaceholder={null}
                    value={value}
                    onChange={onChange}
                  >
                    <Input
                      label="Telefone"
                      placeholder="(00) 00000-0000"
                      isValid={false}
                      errorMessage={error?.message && `${error?.message}`}
                    />
                  </ReactInputMask>
                )}
              />
            </div>
          </div>

          <Controller
            name="linkedin"
            control={control}
            render={({
              field: { value = '', onChange },
              fieldState: { error },
            }) => (
              <Input
                label="LinkedIn"
                placeholder="https://www.linkedin.com/in/example-user-name"
                isValid={false}
                value={value}
                onChange={onChange}
                errorMessage={error?.message && `${error?.message}`}
              />
            )}
          />

          <div className="w-36 md:w-44">
            <Controller
              name="cep"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <ReactInputMask
                  mask="99999-999"
                  maskPlaceholder={null}
                  value={value}
                  onChange={(event) => {
                    onChange(event)

                    const currentValue = event.target.value
                    const cepPattern = /^\d{5}-\d{3}$/

                    if (cepPattern.test(currentValue)) {
                      validateCep(currentValue)
                    }
                  }}
                >
                  <Input
                    label="CEP"
                    placeholder="00000-000"
                    isValid={false}
                    errorMessage={error?.message}
                  />
                </ReactInputMask>
              )}
            />
          </div>
          <div>
            <Controller
              name="street"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <Input
                  label="Rua"
                  placeholder="Rua"
                  isValid={false}
                  value={value}
                  onChange={onChange}
                  errorMessage={error?.message && `${error?.message}`}
                />
              )}
            />
          </div>

          <div className="flex gap-2">
            <div className="w-20 md:w-28">
              <Controller
                name="addressNumber"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    label="Número"
                    placeholder="000"
                    isValid={false}
                    value={value}
                    onChange={onChange}
                    errorMessage={error?.message && `${error?.message}`}
                  />
                )}
              />
            </div>
            <div className="grow">
              <Controller
                name="complement"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    label="Complemento"
                    placeholder="Complemento"
                    isValid={false}
                    value={value}
                    onChange={onChange}
                    errorMessage={error?.message && `${error?.message}`}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex flex-col gap-5 md:flex-row md:gap-2">
            <div className="md:grow">
              <Controller
                name="neighborhood"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    label="Bairro"
                    placeholder="Bairro"
                    isValid={false}
                    value={value}
                    onChange={onChange}
                    errorMessage={error?.message && `${error?.message}`}
                  />
                )}
              />
            </div>
            <div className="md:grow">
              <Controller
                name="city"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    label="Cidade"
                    placeholder="Cidade"
                    isValid={false}
                    value={value}
                    onChange={onChange}
                    errorMessage={error?.message && `${error?.message}`}
                  />
                )}
              />
            </div>
            <div className="md:grow">
              <Controller
                name="state"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    label="Estado"
                    placeholder="Estado"
                    isValid={false}
                    value={value}
                    onChange={onChange}
                    errorMessage={error?.message && `${error?.message}`}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex flex-col gap-5 md:gap-2 md:flex-row">
            <div className="flex-1">
              <Controller
                name="researcherId"
                control={control}
                render={({ field: { onChange }, fieldState: { error } }) => (
                  <FormGroup label="Research Associate" error={error?.message}>
                    <InfiniteSelect
                      placeholder="Selecione o research"
                      searchInputPlaceholder="Buscar pelo researcher"
                      hasNextPage={hasNextPage}
                      defaultValue={
                        data?.researcher?.id ? String(data?.researcher?.id) : ''
                      }
                      options={options}
                      onLoadMoreOptions={fetchNextPage}
                      onValueChange={(value) => onChange(value)}
                      onSearch={(searchTerm: string) =>
                        setSearchResearch(searchTerm)
                      }
                    />
                  </FormGroup>
                )}
              />
            </div>
            <div className="flex-1">
              <Input
                label={'Data de cadastro'}
                defaultValue={`${dayjs(data?.createdAt).format('DD/MM/YYYY')}`}
                isValid={false}
                disabled
              />
            </div>
          </div>

          <div className="flex flex-col gap-5 md:flex-row md:gap-2">
            <div className="md:grow">
              <Controller
                name="researcherAcronym"
                control={control}
                render={({
                  field: { value = '', onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    label="Sigla do researcher"
                    placeholder="Sigla do researcher"
                    isValid={false}
                    value={value}
                    onChange={onChange}
                    errorMessage={error?.message && `${error?.message}`}
                  />
                )}
              />
            </div>
          </div>

          <Controller
            name="agreeParticipateMeet"
            control={control}
            render={({ field: { onChange } }) => (
              <Radio.Root
                name="agreeParticipateMeet"
                onChange={onChange}
                defaultValue={agreeParticipateMeetDefaultValue}
              >
                <p className="text-xs lg:text-base">
                  Aceita participar de reunião:
                </p>
                <div className="flex gap-10 text-xs mt-7 lg:text-base">
                  <Radio.Item id="no_agree_participate_meet" value="false">
                    <Radio.Label htmlFor="no_agree_participate_meet">
                      Não
                    </Radio.Label>
                  </Radio.Item>

                  <Radio.Item id="yes_agree_participate_meet" value="true">
                    <Radio.Label htmlFor="yes_agree_participate_meet">
                      Sim
                    </Radio.Label>
                  </Radio.Item>
                </div>
              </Radio.Root>
            )}
          />

          <Controller
            name="doNotContactTemporarily"
            control={control}
            render={({ field: { onChange } }) => (
              <Radio.Root
                name="doNotContactTemporarily"
                onChange={onChange}
                defaultValue={doNotContactTemporarilyDefaultValue}
              >
                <p className="text-xs lg:text-base">
                  Bloqueado para contato temporário:
                </p>
                <div className="flex gap-10 text-xs mt-7 lg:text-base">
                  <Radio.Item id="no_do_not_contact_temporarily" value="false">
                    <Radio.Label htmlFor="no_do_not_contact_temporarily">
                      Não
                    </Radio.Label>
                  </Radio.Item>

                  <Radio.Item id="yes_do_not_contact_temporarily" value="true">
                    <Radio.Label htmlFor="yes_do_not_contact_temporarily">
                      Sim
                    </Radio.Label>
                  </Radio.Item>
                </div>
              </Radio.Root>
            )}
          />

          <div className="w-36 md:w-44">
            <Controller
              name="lastContactAt"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <ReactInputMask
                  mask="99/99/9999"
                  maskPlaceholder={null}
                  value={value}
                  onChange={onChange}
                >
                  <Input
                    label="Último contato"
                    placeholder="DD/MM/AAAA"
                    isValid={false}
                    errorMessage={error?.message && `${error?.message}`}
                  />
                </ReactInputMask>
              )}
            />
          </div>
        </div>

        <div className="flex flex-row gap-2 mt-14">
          <Button
            type="submit"
            isLoading={isLoading}
            disabled={
              !formState.isValid || Object.entries(formState.errors).length > 0
            }
          >
            Salvar
          </Button>
        </div>
      </form>
    </div>
  )
}
